import { useMemo } from "react";
import {
  GroupingAttributes,
  VariationGroup,
  VariationMembership,
} from "~/common/Store/models/ProductListing";
import { useFeatureFlag } from "~/common/contexts/FeatureFlagProvider";

interface OverrideMap {
  [key: string]: string;
}

interface VariationsAndVirtualGroupingOptionsProps {
  groupingAttributes: GroupingAttributes;
  variationMembership: VariationMembership | undefined;
  isListView?: boolean;
  itemDisplaysBadge: boolean;
}

export const useVariationsAndVirtualGroupingOptionsForOptionLabel = ({
  groupingAttributes,
  variationMembership,
  isListView = false,
  itemDisplaysBadge,
}: VariationsAndVirtualGroupingOptionsProps) => {
  const { evaluateFlag } = useFeatureFlag();
  const cvToggleFlag = evaluateFlag("collapsible-variation-toggle");

  return useMemo(() => {
    // Virtual Grouping
    const { groupMembers } = groupingAttributes;
    const overrideNames = groupingAttributes?.configurationOptionSummary || [];

    const overrideMap: OverrideMap = overrideNames.reduce(
      (acc: OverrideMap, override) => {
        const trimmedName = override.name.trim();
        acc[trimmedName] = override.overrideName;
        return acc;
      },
      {}
    );

    const numberOfGroupMembers = Array.isArray(
      groupMembers?.[0]?.configurationOptions
    )
      ? groupMembers?.[0]?.configurationOptions?.map((i) => i.name.trimEnd())
      : null;

    const vgMembersCount = numberOfGroupMembers?.length ?? 0;
    const uniqueOptions: { [key: string]: Set<string> } = {};

    groupMembers?.forEach((member) => {
      member.configurationOptions.forEach((option) => {
        if (!uniqueOptions[option.name]) {
          uniqueOptions[option.name] = new Set();
        }
        uniqueOptions[option.name]?.add(option.value);
      });
    });

    // Variations
    const { variationGroups = [], hasMembership = false } =
      variationMembership || {};
    const collapsedVariationGroup = variationGroups?.find((x) => x.isCollapsed);
    const colorVariationGroup = variationGroups?.find(
      (x) => !x.isCollapsed && x.optionName === "Color"
    );

    if (hasMembership) {
      if (collapsedVariationGroup) {
        const { collapsedLabelOverride, optionName } = collapsedVariationGroup;
        const displayName =
          cvToggleFlag && collapsedLabelOverride
            ? collapsedLabelOverride
            : optionName;
        collapsedVariationGroup.variationGroupItems.forEach((option) => {
          if (!uniqueOptions[displayName]) {
            uniqueOptions[displayName] = new Set();
          }
          uniqueOptions[displayName]?.add(option.variationText);
        });
      }

      if (colorVariationGroup) {
        colorVariationGroup.variationGroupItems.forEach((option) => {
          if (!uniqueOptions[colorVariationGroup.optionName]) {
            uniqueOptions[colorVariationGroup.optionName] = new Set();
          }
          uniqueOptions[colorVariationGroup.optionName]?.add(
            option.variationText
          );
        });
      }
    }

    // Display logic for option text on productBox
    const numberOfUniqueOptions = Object.keys(uniqueOptions).length;
    const uniqueCounts = Object.keys(uniqueOptions).map((name) => ({
      name,
      count: uniqueOptions[name]?.size ?? 0,
    }));

    const shouldDisplayOptionText = determineOptionTextVisibility(
      numberOfGroupMembers,
      collapsedVariationGroup,
      colorVariationGroup,
      uniqueCounts,
      overrideMap,
      itemDisplaysBadge,
      isListView,
      vgMembersCount
    );

    const wrapperClass = itemDisplaysBadge
      ? "border-l border-solid border-0 pl-1 ml-1 text-gray-800 text-xs font-semibold underline hover:no-underline leading-3"
      : "even:border-l even:border-solid even:border-0 even:pl-1 even:ml-1 text-gray-800 text-xs font-semibold underline hover:no-underline";

    const optionTextWrapperClass = `text-gray-800 text-xs font-semibold underline whitespace-nowrap hover:no-underline
      ${
        itemDisplaysBadge
          ? "border-l border-solid border-0 pl-1 ml-1 leading-3"
          : ""
      }`;

    return {
      uniqueCounts,
      numberOfUniqueOptions,
      wrapperClass,
      overrideMap,
      shouldDisplayOptionText,
      optionTextWrapperClass,
    };
  }, [
    groupingAttributes,
    variationMembership,
    itemDisplaysBadge,
    isListView,
    cvToggleFlag,
  ]);
};

export default useVariationsAndVirtualGroupingOptionsForOptionLabel;

function determineOptionTextVisibility(
  numberOfGroupMembers: string[] | null | undefined,
  collapsedVariationGroup: VariationGroup | undefined,
  colorVariationGroup: VariationGroup | undefined,
  uniqueCounts: { name: string; count: number }[],
  overrideMap: OverrideMap,
  itemDisplaysBadge: boolean,
  isListView: boolean,
  vgMembersCount: number
) {
  // Calculate the total length of the titles for the group members and variation groups
  const titleLength =
    (numberOfGroupMembers?.join("").length ?? 0) +
    ((collapsedVariationGroup?.optionName.length ?? 0) ||
      (colorVariationGroup?.optionName.length ?? 0));
  if (titleLength === 0) return false;

  const overrideNameLength = uniqueCounts.reduce((acc, { name }) => {
    const overrideName = overrideMap[name.trimEnd()];
    const displayName = overrideName ?? name.trimEnd();
    return acc + displayName.length;
  }, 0);

  const textLengthCutoff = itemDisplaysBadge && !isListView ? 7 : 21;
  const maxConfigurations = itemDisplaysBadge && !isListView ? 1 : 2;

  return (
    uniqueCounts.length > 2 ||
    vgMembersCount > maxConfigurations ||
    titleLength >= textLengthCutoff ||
    overrideNameLength >= textLengthCutoff
  );
}
